import {
  useTable,
  Cell,
  Column,
  HeaderGroup,
  Row,
  UseTableInstanceProps,
} from 'react-table'
import {
  Table as JutsuTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableProps as JutsuTableProps,
  TableHeadProps,
  TableBodyProps,
  TableRowProps,
  TableColumnHeaderProps,
  TableCellProps,
  Text,
} from '@ritualco/jutsu'

export type BasicTableHeaderProps<D extends object = {}> = {
  headers?: 'all' | 'first' | 'none'
  theadProps?: () => TableHeadProps
  theadTrProps?: (headerGroup: HeaderGroup<D>) => TableRowProps
  thProps?: (column: HeaderGroup<D>) => TableColumnHeaderProps
} & Pick<UseTableInstanceProps<D>, 'headerGroups'>

export const BasicTableHeader = <D extends object = {}>(
  props: BasicTableHeaderProps<D>
) => {
  const {
    headers,
    headerGroups,
    theadProps = () => {},
    theadTrProps = () => {},
    thProps = () => {},
  } = props
  if (headers === 'none') {
    return null
  }
  const headerGroupsToRender =
    headers === 'all' ? headerGroups : [headerGroups[0]]
  return (
    <Thead {...theadProps()}>
      {headerGroupsToRender.map(headerGroup => (
        <Tr
          {...headerGroup.getHeaderGroupProps()}
          {...theadTrProps(headerGroup)}
        >
          {headerGroup.headers.map(column => (
            <Th {...column.getHeaderProps()} {...thProps(column)}>
              {column.render('Header')}
            </Th>
          ))}
        </Tr>
      ))}
    </Thead>
  )
}

export type BasicTableProps<D extends object = {}> = {
  columns: Column<D>[]
  data: Array<D>
  headers?: 'all' | 'first' | 'none'
  /** Text shown when there are no rows to display */
  placeholder?: string
  tableProps?: () => JutsuTableProps
  theadProps?: () => TableHeadProps
  theadTrProps?: (headerGroup: HeaderGroup<D>) => TableRowProps
  thProps?: (column: HeaderGroup<D>) => TableColumnHeaderProps
  tbodyProps?: () => TableBodyProps
  trProps?: (row: Row<D>) => TableRowProps
  tdProps?: (cell: Cell<D>) => TableCellProps
}

export const BasicTable = <D extends object = {}>(
  props: BasicTableProps<D>
) => {
  const {
    columns,
    data,
    headers = 'first',
    placeholder,
    tableProps = () => {},
    theadProps,
    theadTrProps,
    thProps,
    tbodyProps = () => {},
    trProps = () => {},
    tdProps = () => {},
  } = props
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    visibleColumns,
    rows,
    prepareRow,
  } = useTable({ columns, data })

  return (
    <JutsuTable {...getTableProps()} {...tableProps()}>
      <BasicTableHeader
        headers={headers}
        headerGroups={headerGroups}
        theadProps={theadProps}
        theadTrProps={theadTrProps}
        thProps={thProps}
      />
      <Tbody {...getTableBodyProps()} {...tbodyProps()}>
        {rows.length === 0 ? (
          <Tr>
            <Td colSpan={visibleColumns.length} color="grayscale.secondary">
              <Text whiteSpace="pre-line">{placeholder}</Text>
            </Td>
          </Tr>
        ) : (
          rows.map(row => {
            prepareRow(row)
            return (
              <Tr {...row.getRowProps()} {...trProps(row)}>
                {row.cells.map(cell => (
                  <Td {...cell.getCellProps()} {...tdProps(cell)}>
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            )
          })
        )}
      </Tbody>
    </JutsuTable>
  )
}
